import React, { useEffect, useState } from "react";
import api from "@/lib/api";

function isoToDmy(iso) {
  if (!iso || !iso.includes("-")) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const GIORNI_IT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
function dmyWeekday(dmy) {
  if (!dmy || !dmy.includes("/")) return "";
  const [d, m, y] = dmy.split("/").map(Number);
  if (!d || !m || !y) return "";
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return "";
  return GIORNI_IT[dt.getDay()];
}

export default function MensaStoricoPage() {
  const [storico, setStorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (dataDa) params.data_da = dataDa;
      if (dataA) params.data_a = dataA;
      const s = await api.get("/mensa/giorno/storico", { params });
      setStorico(s.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento storico");
    } finally { setLoading(false); }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [dataDa, dataA]);

  const eliminaStorico = async (data_iso) => {
    if (!window.confirm(`Eliminare la storicizzazione del ${isoToDmy(data_iso)}?`)) return;
    try {
      await api.delete(`/mensa/giorno/storico/${data_iso}`);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const scaricaFile = async (data_iso) => {
    try {
      const r = await api.get("/mensa/giorno/file", { params: { data_iso }, responseType: "blob" });
      const ct = r.headers["content-type"] || "application/vnd.ms-excel";
      const blob = new Blob([r.data], { type: ct });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `mensa_${data_iso}.xls`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        alert("Nessun file Excel originale conservato per questa data.\nLa conservazione è attiva solo per import successivi alla nuova versione.");
      } else {
        alert("Errore download: " + (e.response?.data?.detail || e.message));
      }
    }
  };

  const totOre10 = storico.reduce((a, s) => a + (s.tot_ore10 || 0), 0);
  const totOre12 = storico.reduce((a, s) => a + (s.tot_ore12 || 0), 0);
  const totDiffCom = storico.reduce((a, s) => {
    const n = Number(s.differenza_comunicata);
    return a + (Number.isFinite(n) ? n : 0);
  }, 0);

  const eliminaFinoAOggi = async () => {
    const t = new Date();
    const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    const n = storico.filter(s => (s.data_iso || "") < iso).length;
    if (n === 0) { alert("Nessuna giornata precedente a oggi da eliminare."); return; }
    if (!window.confirm(`Eliminare ${n} giornate storicizzate precedenti a oggi (${isoToDmy(iso)})?\nL'operazione non è reversibile.`)) return;
    try {
      const r = await api.delete("/mensa/giorno/storico", { params: { before: iso } });
      alert(`Eliminate ${r.data.deleted} giornate storicizzate.`);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione in blocco");
    }
  };

  const salvaDifferenza = async (data_iso, valore) => {
    try {
      await api.put(`/mensa/giorno/storico/${data_iso}/differenza`, { valore });
      setStorico(prev => prev.map(s => s.data_iso !== data_iso ? s : { ...s, differenza_comunicata: valore }));
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio differenza");
    }
  };

  return (
    <div>
      <div className="sw-page-title">
        <h1>📚 Storico Mensa</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#666" }}>{storico.length} giornate registrate</span>
          <button onClick={carica} data-testid="ms-reload"
            style={{ padding: "6px 14px", background: "#0066cc", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            ↻ Aggiorna
          </button>
        </div>
      </div>

      <div style={{ margin: "0 20px 8px", padding: "8px 12px", background: "#ede7f6", border: "1px solid #b39ddb", color: "#311b92", fontSize: 11 }}>
        I dati storicizzati derivano dalla pagina <b>Presenze Mensa</b> · pulsante <b>📚 Storicizza dato</b>.
        Viene registrata la <b>comparazione delle rilevazioni Ore 10 / Ore 12</b> per plesso. Il campo <b>Diff. comunicata alla mensa</b> è un <b>unico valore per giornata</b> che <b>inserisci a mano</b>, resta salvato ed è <b>totalizzato in fondo alla lista</b>.
      </div>

      {/* Filtri data */}
      <div style={{ margin: "0 20px 10px", padding: 10, background: "white", border: "1px solid #cfd8dc", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#37474f" }}>DATA DA:</span>
          <input type="date" value={dataDa} onChange={e => setDataDa(e.target.value)} data-testid="ms-data-da"
            style={{ padding: "4px 8px", border: "1px solid #999", fontSize: 12 }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#37474f" }}>DATA A:</span>
          <input type="date" value={dataA} onChange={e => setDataA(e.target.value)} data-testid="ms-data-a"
            style={{ padding: "4px 8px", border: "1px solid #999", fontSize: 12 }} />
        </div>
        {(dataDa || dataA) && (
          <button onClick={() => { setDataDa(""); setDataA(""); }} data-testid="ms-data-clear"
            style={{ padding: "4px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            ✕ Reset filtri data
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={eliminaFinoAOggi} data-testid="ms-del-before-today"
          title="Elimina in blocco tutte le giornate storicizzate precedenti alla data odierna (oggi escluso)"
          style={{ padding: "6px 14px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          🗑 Elimina giornate fino a oggi (escluso)
        </button>
      </div>

      {err && (
        <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }} data-testid="ms-error">{err}</div>
      )}

      {/* Storico comparazione rilevazioni Ore 10 / Ore 12 (full width) */}
      <div style={{ margin: "0 20px 20px" }}>
        <div style={{ background: "white", border: "1px solid #4527a0" }} data-testid="ms-storico-panel">
          <div style={{ padding: "8px 12px", background: "#4527a0", color: "white", fontSize: 12, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>📚 Storico comparazione rilevazioni Ore 10 / Ore 12</span>
            <span style={{ fontSize: 11, fontWeight: 400 }}>{storico.length} giornate registrate</span>
          </div>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12 }}>Caricamento...</div>
          ) : storico.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>
              Nessuna giornata storicizzata. Vai in <b>Presenze Mensa</b> e usa il pulsante <b>📚 Storicizza dato</b> per registrare la comparazione delle rilevazioni del giorno.
            </div>
          ) : (
            <div style={{ maxHeight: "72vh", overflow: "auto" }}>
              <table className="sw-table sw-table-compact" style={{ fontSize: 13, width: "100%" }}>
                <thead style={{ position: "sticky", top: 0, background: "#311b92", color: "white", zIndex: 1 }}>
                  <tr>
                    <th style={{ minWidth: 160 }}>DATA</th>
                    <th style={{ minWidth: 160 }}>PLESSO</th>
                    <th style={{ width: 110, textAlign: "center" }}>ORE 10</th>
                    <th style={{ width: 110, textAlign: "center" }}>ORE 12</th>
                    <th style={{ width: 110, textAlign: "center" }}>DIFF.</th>
                    <th style={{ width: 220, textAlign: "center" }}>DIFF. COMUNICATA ALLA MENSA</th>
                    <th style={{ width: 110, textAlign: "center" }}>FILE / —</th>
                  </tr>
                </thead>
                <tbody>
                  {storico.map((s, i) => {
                    const pp = s.per_plesso && s.per_plesso.length ? s.per_plesso : [{ plesso: "—", ore10: s.tot_ore10 || 0, ore12: (s.tot_ore12 ?? s.tot_totale) || 0, delta: s.delta ?? 0 }];
                    const d = s.data || isoToDmy(s.data_iso); const wd = dmyWeekday(d);
                    return pp.map((p, j) => (
                      <tr key={`${s.data_iso}-${p.plesso}`} data-testid={`ms-row-${i}-${j}`} style={{ background: j === 0 ? "#faf9ff" : "white" }}>
                        {j === 0 && (
                          <td rowSpan={pp.length} style={{ fontFamily: "Consolas, monospace", fontWeight: 700, verticalAlign: "top", borderRight: "2px solid #ede7f6" }}>
                            {wd ? `${wd} ${d}` : d}
                            <div style={{ fontSize: 10, fontWeight: 400, color: "#777", marginTop: 4 }}>Ore10: {s.tot_ore10 ?? 0} · Ore12: {s.tot_ore12 ?? 0}</div>
                          </td>
                        )}
                        <td style={{ fontFamily: "Consolas, monospace" }}>{p.plesso}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", color: "#1565c0", fontWeight: 700 }}>{p.ore10}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", color: "#2e7d32", fontWeight: 700 }}>{p.ore12}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 800, color: (p.delta || 0) === 0 ? "#607d8b" : ((p.delta || 0) > 0 ? "#2e7d32" : "#c62828") }}>{(p.delta || 0) >= 0 ? "+" : ""}{p.delta || 0}</td>
                        {j === 0 && (
                          <td rowSpan={pp.length} style={{ textAlign: "center", verticalAlign: "middle", background: "#fff8e1" }}>
                            <input data-testid={`ms-diffcom-${i}`} defaultValue={s.differenza_comunicata ?? ""}
                              onBlur={(e) => { const v = e.target.value.trim(); salvaDifferenza(s.data_iso, v === "" ? "" : (isNaN(Number(v)) ? v : Number(v))); }}
                              title="Differenza effettivamente comunicata alla mensa — valore unico per la giornata (modificabile)"
                              style={{ width: 120, textAlign: "center", padding: "3px 4px", border: "1px solid #e0b000", borderRadius: 3, fontFamily: "Consolas, monospace", fontWeight: 700 }} />
                          </td>
                        )}
                        {j === 0 && (
                          <td rowSpan={pp.length} style={{ textAlign: "center", verticalAlign: "top" }}>
                            <button onClick={() => scaricaFile(s.data_iso)} data-testid={`ms-dl-${i}`} title="Scarica il file Excel originale"
                              style={{ padding: "2px 8px", background: "#2e7d32", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginBottom: 3 }}>📥</button>
                            <br />
                            <button onClick={() => eliminaStorico(s.data_iso)} data-testid={`ms-del-${i}`} title="Elimina questa storicizzazione"
                              style={{ padding: "2px 8px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#ede7f6", fontWeight: 800, color: "#311b92" }}>
                    <td colSpan={2} style={{ padding: "6px 8px", textAlign: "right" }}>TOTALI COMPLESSIVI</td>
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", color: "#1565c0" }}>{totOre10}</td>
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", color: "#2e7d32" }}>{totOre12}</td>
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace" }}>{(totOre12 - totOre10) >= 0 ? "+" : ""}{totOre12 - totOre10}</td>
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", background: "#fff3c4", color: "#6d4c00" }} data-testid="ms-tot-diffcom">{totDiffCom}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
